import { useEffect, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import clsx from 'clsx'
import { ChevronDown, Menu, X } from 'lucide-react'
import { PopupModal } from 'react-calendly'
import { NavBrandLink } from '@/components/BenepicksHeader/NavBrandLink'
import { useCalendlyRoot } from '../hooks/useCalendlyRoot'
import { useFlags } from '../context/FlagsContext'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

const CALENDLY_URL = 'https://calendly.com/benepicks/demo'
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)'
const BROKER_LOGIN_PATH = '/brokers/sign_in'

const primaryActionClasses = clsx(
  'rounded-full bg-orange-hover text-white shadow-sm',
  'hover:bg-[#d96532] hover:text-white',
  'focus-visible:ring-2 focus-visible:ring-orange-hover focus-visible:ring-offset-2',
  'transition-colors duration-200'
)

const secondaryActionClasses = clsx(
  'rounded-full border-neutral-300 bg-white text-neutral-800 shadow-sm',
  'hover:border-neutral-400 hover:bg-neutral-50 hover:text-neutral-900',
  'focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2',
  'transition-colors duration-200'
)

const loginOptions = [
  {
    label: 'Employer',
    description: 'Manage allowances, enrollment, and billing.',
    href: '/executives/sign_in',
  },
  {
    label: 'Employee',
    description: 'See your allowance and shop for plans.',
    href: '/employees/sign_in',
  },
]

export const Navigation = () => {
  const { brokerPortalEnabled } = useFlags()
  const rootElement = useCalendlyRoot()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [calendlyOpen, setCalendlyOpen] = useState(false)
  const [calendlyRequested, setCalendlyRequested] = useState(false)

  useEffect(() => {
    if (!window.matchMedia) return

    const desktopBreakpoint = window.matchMedia(DESKTOP_MEDIA_QUERY)
    const closeMobileMenu = (event: MediaQueryListEvent) => {
      if (event.matches) setMobileMenuOpen(false)
    }

    desktopBreakpoint.addEventListener('change', closeMobileMenu)
    return () =>
      desktopBreakpoint.removeEventListener('change', closeMobileMenu)
  }, [])

  const openCalendlyFromDesktop = () => setCalendlyOpen(true)

  const requestCalendlyFromMobile = () => {
    setCalendlyRequested(true)
    setMobileMenuOpen(false)
  }

  const handleMobileMenuCloseAutoFocus = (event: Event) => {
    if (!calendlyRequested) return

    event.preventDefault()
    setCalendlyRequested(false)
    setCalendlyOpen(true)
  }

  return (
    <>
      <nav
        aria-label="Primary navigation"
        className={clsx(
          'fixed top-0 left-0 right-0 z-50',
          'bg-white/60 backdrop-blur-2xl backdrop-saturate-150',
          'border-b border-white/40',
          'shadow-[0_1px_3px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.6)]'
        )}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 sm:h-[72px] items-center justify-between">
            <NavBrandLink />

            <div className="hidden items-center gap-6 md:flex">
              <a
                href="/blog"
                className="rounded-sm font-body subpixel-antialiased text-sm text-neutral-600 transition-colors duration-200 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
              >
                Resources
              </a>

              <Button
                size="lg"
                className={clsx(primaryActionClasses, 'h-10 px-5')}
                onClick={openCalendlyFromDesktop}
                disabled={!rootElement}
              >
                Book a Demo
              </Button>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className={clsx(secondaryActionClasses, 'h-10 px-5')}
                  >
                    Log In
                    <ChevronDown
                      className="h-3.5 w-3.5 opacity-60"
                      aria-hidden="true"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  sideOffset={10}
                  className="w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-neutral-200 bg-white p-2 shadow-[0_18px_50px_-18px_rgba(16,46,44,.35)]"
                >
                  <div className="px-3 pb-2 pt-1.5 font-body text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                    Log in to Benepicks
                  </div>
                  <div className="flex flex-col gap-1">
                    {loginOptions.map((option) => (
                      <a
                        key={option.href}
                        href={option.href}
                        className="rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
                      >
                        <span className="block font-body text-sm font-semibold text-neutral-900">
                          {option.label}
                        </span>
                        <span className="mt-0.5 block font-body text-xs leading-snug text-neutral-500">
                          {option.description}
                        </span>
                      </a>
                    ))}
                  </div>
                  {brokerPortalEnabled && (
                    <div className="mt-2 border-t border-neutral-200 px-3 pb-2 pt-3 font-body text-sm text-neutral-600">
                      <span>Broker? </span>
                      <a
                        href={BROKER_LOGIN_PATH}
                        className="font-medium text-neutral-900 underline underline-offset-2 transition-colors hover:text-orange-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
                      >
                        Log in through the broker portal
                      </a>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>

            <DialogPrimitive.Root
              open={mobileMenuOpen}
              onOpenChange={setMobileMenuOpen}
            >
              <DialogPrimitive.Trigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-full text-neutral-800 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2 md:hidden"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-6 w-6" aria-hidden="true" />
                </Button>
              </DialogPrimitive.Trigger>

              <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-neutral-950/35 data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 md:hidden" />
                <DialogPrimitive.Content
                  aria-describedby={undefined}
                  onCloseAutoFocus={handleMobileMenuCloseAutoFocus}
                  className="fixed inset-y-0 right-0 z-[70] flex h-dvh w-[min(88vw,24rem)] flex-col overflow-y-auto overscroll-contain border-l border-neutral-200 bg-white p-5 shadow-2xl outline-none data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right md:hidden"
                >
                  <div className="flex items-center justify-between border-b border-neutral-200 pb-4">
                    <DialogPrimitive.Title className="font-sans text-lg font-semibold text-neutral-900">
                      Menu
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Close asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 rounded-full text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
                        aria-label="Close navigation menu"
                      >
                        <X className="h-6 w-6" aria-hidden="true" />
                      </Button>
                    </DialogPrimitive.Close>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 pt-6">
                    <a
                      href="/blog"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex min-h-11 items-center rounded-lg px-4 font-body text-base font-medium text-neutral-700 transition-colors hover:bg-neutral-50 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
                    >
                      Resources
                    </a>

                    <div className="mt-auto flex flex-col gap-3 border-t border-neutral-200 pt-5">
                      <Button
                        className={clsx(
                          primaryActionClasses,
                          'h-12 w-full text-base'
                        )}
                        onClick={requestCalendlyFromMobile}
                        disabled={!rootElement}
                      >
                        Book a Demo
                      </Button>

                      <div className="rounded-2xl border border-neutral-200 bg-neutral-50/60 p-2">
                        <p className="px-2 pb-2 pt-1 font-body text-xs font-semibold uppercase tracking-[0.08em] text-neutral-500">
                          Log in to Benepicks
                        </p>
                        <div className="flex flex-col gap-2">
                          {loginOptions.map((option) => (
                            <a
                              key={option.href}
                              href={option.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="rounded-xl border border-neutral-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-neutral-300 hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
                            >
                              <span className="block font-body text-base font-semibold text-neutral-900">
                                {option.label}
                              </span>
                              <span className="mt-0.5 block font-body text-sm leading-snug text-neutral-500">
                                {option.description}
                              </span>
                            </a>
                          ))}
                        </div>
                        {brokerPortalEnabled && (
                          <div className="mt-3 border-t border-neutral-200 px-2 pb-1 pt-3 font-body text-sm text-neutral-600">
                            <span>Broker? </span>
                            <a
                              href={BROKER_LOGIN_PATH}
                              onClick={() => setMobileMenuOpen(false)}
                              className="font-medium text-neutral-900 underline underline-offset-2 transition-colors hover:text-orange-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary focus-visible:ring-offset-2"
                            >
                              Log in through the broker portal
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </DialogPrimitive.Content>
              </DialogPrimitive.Portal>
            </DialogPrimitive.Root>
          </div>
        </div>
      </nav>

      {rootElement && (
        <PopupModal
          url={CALENDLY_URL}
          open={calendlyOpen}
          rootElement={rootElement}
          onModalClose={() => setCalendlyOpen(false)}
        />
      )}
    </>
  )
}
